import { useState, useEffect, useCallback } from "react";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { DemoModeProvider, useDemoMode } from "./lib/DemoModeContext";
import { FeatureFlagsProvider, useFeatureFlags } from "./lib/FeatureFlagsContext";
import { RolePreviewProvider } from "./lib/RolePreviewContext";
import { config } from "./lib/config";
import { supabase } from "./lib/supabase";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { Home } from "./pages/Home";
import { extractHashParams, detectSafeLinks } from "./lib/utils/safelinks";
import { DevModePanel } from "./components/DevModePanel";
import { RolePreviewBanner } from "./components/RolePreviewBanner";
import { A11yEnhancements } from "./components/A11yEnhancements";

// SEO Metadata
const updateMetaTags = (title: string, description: string) => {
  document.title = title;
  
  // Update or create meta description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }
  metaDescription.setAttribute('content', description);
  
  // Update or create OG meta tags for social sharing
  const ogTitle = document.querySelector('meta[property="og:title"]') || document.createElement('meta');
  ogTitle.setAttribute('property', 'og:title');
  ogTitle.setAttribute('content', title);
  if (!document.querySelector('meta[property="og:title"]')) {
    document.head.appendChild(ogTitle);
  }
  
  const ogDescription = document.querySelector('meta[property="og:description"]') || document.createElement('meta');
  ogDescription.setAttribute('property', 'og:description');
  ogDescription.setAttribute('content', description);
  if (!document.querySelector('meta[property="og:description"]')) {
    document.head.appendChild(ogDescription);
  }
};
import { FAQ } from "./pages/FAQ";
import { Privacy } from "./pages/Privacy";
import { SignUp } from "./pages/SignUp";
import { SignIn } from "./pages/SignIn";
import { EmailVerification } from "./pages/EmailVerification";
import { Consent } from "./pages/Consent";
import { Debug } from "./pages/Debug";
import { Discover } from "./pages/Discover";
import { ProductDetail } from "./pages/ProductDetail";
import { BatchDetail } from "./pages/BatchDetail";
import { SubmitReview } from "./pages/SubmitReview";
import { ReviewDetail } from "./pages/ReviewDetail";
import { ClinicsIndex } from "./pages/ClinicsIndex";
import { ProductsIndex } from "./pages/ProductsIndex";
import { AccountSettings } from "./pages/AccountSettings";
import { Terms } from "./pages/Terms";
import { Moderation } from "./pages/Moderation";
import { Transparency } from "./pages/Transparency";
import { AdminDashboard } from "./pages/AdminDashboard";
import { AdminVerify } from "./pages/AdminVerify";
import { AdminOrg } from "./pages/AdminOrg";
import { AdminCatalog } from "./pages/AdminCatalog";
import { AdminResponses } from "./pages/AdminResponses";
import { ModerationQueue } from "./pages/ModerationQueue";
import { ClinicDetail } from "./pages/ClinicDetail";
import { ClinicDashboard } from "./pages/ClinicDashboard";
import { ClinicProductDetail } from "./pages/ClinicProductDetail";
import PatientVerification from "./pages/PatientVerification";
import { EmailTest } from "./pages/EmailTest";
import ProjectStatus from "./pages/ProjectStatus";
import { Accessibility } from "./pages/Accessibility";
import { VerifyQR } from "./pages/VerifyQR";
import { ScanVerify } from "./pages/ScanVerify";
import { OrgSettings } from "./pages/OrgSettings";
import { OrgMembers } from "./pages/OrgMembers";
import { ClinicVerification } from "./pages/ClinicVerification";
import { Press } from "./pages/Press";
import { ForClinics } from "./pages/ForClinics";
import CareersPage from "./pages/CareersPage";
import AmbassadorRolePage from "./pages/AmbassadorRolePage";

// Main App component that uses auth
function AppContent() {
  const { user, profile, loading: authLoading } = useAuth();
  const { isDemoMode } = useDemoMode();
  const { isEnabled } = useFeatureFlags();
  
  // Parse initial route from URL hash
  const getInitialPage = () => {
    // 🔓 SAFELINKS FIX: Check for and unwrap Microsoft Outlook SafeLinks
    const isSafeLinks = detectSafeLinks();
    if (isSafeLinks) {
      console.log('🔓 Microsoft Outlook SafeLinks detected - unwrapping URL');
    }
    
    // Extract hash with SafeLinks unwrapping
    const { hash, params: hashParams, wasUnwrapped } = extractHashParams(window.location.href);
    const search = window.location.search; // Get query params too
    
    // 🔍 COMPREHENSIVE URL DEBUGGING
    console.log('='.repeat(80));
    console.log('🌐 ROUTE DETECTION - Parsing URL');
    console.log('='.repeat(80));
    console.log('📍 Full URL:', window.location.href);
    console.log('🔗 Protocol:', window.location.protocol);
    console.log('🌍 Hostname:', window.location.hostname);
    console.log('📂 Pathname:', window.location.pathname);
    console.log('🔍 Search params:', window.location.search);
    console.log('#️⃣ Hash:', window.location.hash);
    console.log('🧩 Hash (cleaned):', hash.substring(0, 200));
    if (wasUnwrapped) {
      console.log('🔓 SafeLinks: URL was unwrapped from Outlook wrapper');
    }
    console.log('='.repeat(80));
    
    // Check for auth errors first (using unwrapped params)
    if (hashParams.has('error') || search.includes('error=')) {
      console.error('❌ Auth error detected in URL');
      const errorType = hashParams.get('error') || new URLSearchParams(search.slice(1)).get('error');
      const errorDesc = hashParams.get('error_description') || new URLSearchParams(search.slice(1)).get('error_description');
      console.error('Auth error:', errorType, errorDesc);
      return "verify"; // Show error on verify page
    }
    
    // Check if this is a Supabase auth callback (magic link)
    // Supabase appends tokens directly to hash when redirect URL is base domain
    // Format: https://cannabias.co.uk/#access_token=xxx&expires_in=3600&refresh_token=xxx&token_type=bearer&type=magiclink
    const isAuthCallback = 
      hashParams.has('access_token') || 
      hashParams.has('type') ||
      hashParams.has('token') ||
      search.includes('token=') ||
      search.includes('access_token=');
    
    if (isAuthCallback) {
      console.log('🔐 Auth callback detected - routing to verify');
      console.log('🔑 Auth tokens found in URL');
      return "verify";
    }
    
    if (hash.startsWith('/')) {
      // Extract just the route path, ignore query params
      const path = hash.slice(1).split('?')[0];
      
      // Parse path segments to extract IDs (e.g., /clinic-product-detail/product-id)
      const segments = path.split('/');
      const pageName = segments[0];
      const pageId = segments[1];
      
      console.log('📍 Routing to:', pageName || 'home', pageId ? `(ID: ${pageId})` : '');
      
      // Store the ID if present
      if (pageId) {
        setTimeout(() => setSelectedId(pageId), 0);
      }
      
      return pageName || "home";
    }
    
    console.log('📍 Routing to: home (default)');
    return "home";
  };

  const [currentPage, setCurrentPage] = useState(getInitialPage());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hasShownSuccessToast, setHasShownSuccessToast] = useState(false);
  const [authDelayComplete, setAuthDelayComplete] = useState(false);

  // 🚀 CRITICAL: Capture session immediately on mount
  useEffect(() => {
    const captureSessionOnMount = async () => {
      console.log('🚀 IMMEDIATE SESSION CAPTURE - Running on mount');
      console.log('📍 Current URL:', window.location.href);
      
      // 🔓 SAFELINKS FIX: Unwrap SafeLinks before checking tokens
      const { params: hashParams, wasUnwrapped } = extractHashParams(window.location.href);
      
      if (wasUnwrapped) {
        console.log('🔓 SafeLinks unwrapped on mount - this was from Outlook');
      }
      
      const hasTokens = hashParams.has('access_token') && hashParams.has('refresh_token');
      
      if (hasTokens) {
        console.log('🔑 Auth tokens detected in URL - attempting immediate session capture');
        try {
          const { data, error } = await supabase.auth.getSession();
          if (data.session) {
            console.log('✅ Session captured successfully:', data.session.user?.email);
          } else if (error) {
            console.error('❌ Error capturing session:', error);
          } else {
            console.log('⚠️ No session returned from getSession - will be handled by AuthContext');
          }
        } catch (err) {
          console.error('❌ Exception during session capture:', err);
        }
      }
    };
    
    captureSessionOnMount();
  }, []); // Run once on mount

  // Log auth state for debugging
  useEffect(() => {
    if (!authLoading) {
      console.log('👤 Auth State:', {
        isLoggedIn: !!user,
        hasProfile: !!profile,
        userEmail: user?.email,
        userRole: profile?.role,
        hasConsent: !!profile?.consented_at,
        isDemoMode,
      });
    }
  }, [user, profile, authLoading, isDemoMode]);

  // Show success toast when user successfully authenticates and add delay
  useEffect(() => {
    if (user && !authLoading && !hasShownSuccessToast) {
      console.log('✅ Authentication successful - showing success toast');
      toast.success(`Successfully logged in as ${user.email}!`, {
        duration: 4000,
        description: 'Welcome to CannaBias',
      });
      setHasShownSuccessToast(true);
      
      // 🚀 CRITICAL: Add 2-second delay to ensure session is fully established
      console.log('⏳ Adding 2-second delay before allowing redirects...');
      setTimeout(() => {
        console.log('✅ Auth delay complete - session fully established');
        setAuthDelayComplete(true);
      }, 2000);
    }
  }, [user, authLoading, hasShownSuccessToast]);

  // Handle hash changes (for browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      // 🔓 SAFELINKS FIX: Unwrap SafeLinks on hash change
      const { hash, params: hashParams, wasUnwrapped } = extractHashParams(window.location.href);
      const search = window.location.search;
      
      console.log('='.repeat(80));
      console.log('🔄 HASH CHANGE EVENT DETECTED');
      console.log('='.repeat(80));
      console.log('📍 Full URL:', window.location.href);
      console.log('#️⃣ Hash:', window.location.hash);
      console.log('🔍 Search:', window.location.search);
      console.log('🧩 Hash (cleaned):', hash.substring(0, 200));
      if (wasUnwrapped) {
        console.log('🔓 SafeLinks: URL unwrapped on hash change');
      }
      console.log('='.repeat(80));
      
      // Check for auth errors
      if (hashParams.has('error') || search.includes('error=')) {
        console.error('❌ Auth error in hash change');
        setCurrentPage("verify");
        return;
      }
      
      // Check if this is a Supabase auth callback
      const isAuthCallback = 
        hashParams.has('access_token') || 
        hashParams.has('type') ||
        hashParams.has('token') ||
        search.includes('token=') ||
        search.includes('access_token=');
      
      if (isAuthCallback) {
        console.log('🔐 Auth callback in hash change - routing to verify');
        setCurrentPage("verify");
        return;
      }
      
      if (hash.startsWith('/')) {
        // Extract just the route path, ignore query params
        const path = hash.slice(1).split('?')[0];
        
        // Parse path segments to extract IDs
        const segments = path.split('/');
        const pageName = segments[0];
        const pageId = segments[1];
        
        console.log('📍 Hash change routing to:', pageName || 'home', pageId ? `(ID: ${pageId})` : '');
        
        // Update page and ID
        setCurrentPage(pageName || "home");
        setSelectedId(pageId || null);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);
  
  // Update SEO meta tags based on current page
  useEffect(() => {
    if (currentPage === "home" || currentPage === "") {
      updateMetaTags(
        "Medical cannabis reviews by patients (UK) | CannaBias",
        "Patient-led product and batch reviews for prescribed cannabis in the UK. Transparent, moderated, privacy-first."
      );
    }
  }, [currentPage]);

  // 🔴 BUG FIX #3: Memoize handleNavigate to prevent infinite redirect loops
  const handleNavigate = useCallback((page: string, id?: string) => {
    setCurrentPage(page);
    setSelectedId(id || null);
    
    // Update URL hash for browser history
    window.location.hash = `#/${page}`;
    
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []); // No dependencies needed - setters are stable

  const renderPage = () => {
    switch (currentPage) {
      case "faq":
        return <FAQ />;
      case "privacy":
        return <Privacy />;
      case "signup":
        return <SignUp onNavigate={handleNavigate} />;
      case "signin":
        return <SignIn onNavigate={handleNavigate} />;
      case "verify":
        return (
          <EmailVerification onNavigate={handleNavigate} />
        );
      case "consent":
        return <Consent onNavigate={handleNavigate} />;
      case "discover":
        return <Discover onNavigate={handleNavigate} />;
      case "product-detail":
        return (
          <ProductDetail
            productId={selectedId || undefined}
            onNavigate={handleNavigate}
          />
        );
      case "batch-detail":
        return (
          <BatchDetail
            batchId={selectedId || undefined}
            onNavigate={handleNavigate}
          />
        );
      case "submit-review":
        return <SubmitReview productId={selectedId || undefined} onNavigate={handleNavigate} />;
      case "review-detail":
        return (
          <ReviewDetail
            reviewId={selectedId || undefined}
            onNavigate={handleNavigate}
          />
        );
      case "clinics":
        return <ClinicsIndex onNavigate={handleNavigate} />;
      case "products":
        return <ProductsIndex onNavigate={handleNavigate} />;
      case "account":
        return <AccountSettings onNavigate={handleNavigate} />;
      case "terms":
        return <Terms onNavigate={handleNavigate} />;
      case "accessibility":
        return <Accessibility onNavigate={handleNavigate} />;
      case "moderation":
        return <Moderation onNavigate={handleNavigate} />;
      case "transparency":
        return <Transparency onNavigate={handleNavigate} />;
      case "admin":
        return <AdminDashboard onNavigate={handleNavigate} />;
      case "admin-verify":
        return <AdminVerify onNavigate={handleNavigate} />;
      case "admin-org":
        return <AdminOrg onNavigate={handleNavigate} />;
      case "admin-catalog":
        return <AdminCatalog onNavigate={handleNavigate} />;
      case "admin-responses":
        return <AdminResponses onNavigate={handleNavigate} />;
      case "moderation-queue":
        return <ModerationQueue onNavigate={handleNavigate} />;
      case "clinic-detail":
        return (
          <ClinicDetail
            clinicId={selectedId || undefined}
            onNavigate={handleNavigate}
          />
        );
      case "clinic-dashboard":
        return <ClinicDashboard />;
      case "clinic-product-detail":
        return (
          <ClinicProductDetail
            productId={selectedId || undefined}
          />
        );
      case "clinic-verification":
        return <ClinicVerification />;
      case "org-settings":
      case "app/org/settings":
        return <OrgSettings onNavigate={handleNavigate} />;
      case "org-members":
      case "app/org/members":
        return <OrgMembers onNavigate={handleNavigate} />;
      case "verify-qr":
      case "app/verify":
        return <VerifyQR onNavigate={handleNavigate} />;
      case "scan-verify":
      case "scan":
        return <ScanVerify onNavigate={handleNavigate} />;
      case "patient-verification":
        return <PatientVerification />;
      // Development-only pages - redirect to home in production
      case "email-test":
        return config.isDevelopment ? <EmailTest /> : <Home onNavigate={handleNavigate} />;
      case "debug":
        return config.isDevelopment ? <Debug /> : <Home onNavigate={handleNavigate} />;
      case "project-status":
        return config.isDevelopment ? <ProjectStatus onNavigate={handleNavigate} /> : <Home onNavigate={handleNavigate} />;
      case "press":
        return <Press />;
      case "for-clinics":
        return <ForClinics />;
      case "careers":
        return <CareersPage onNavigate={handleNavigate} />;
      case "careers/ambassador":
      case "ambassador-role":
        return <AmbassadorRolePage onNavigate={handleNavigate} />;
      default:
        return <Home onNavigate={handleNavigate} />;
    }
  };

  // Define page access rules
  // Debug pages only accessible in development mode
  const devOnlyPages = config.isDevelopment ? ["debug", "email-test", "project-status"] : [];
  const publicPages = ["home", "", "faq", "privacy", "terms", "transparency", "moderation", "press", "accessibility", "for-clinics", "careers", "careers/ambassador", "ambassador-role", ...devOnlyPages];
  const authPages = ["signup", "signin", "verify"];
  const protectedPages = ["discover", "submit-review", "account", "patient-verification", "scan-verify", "scan"];
  const adminPages = ["admin", "admin-verify", "admin-org", "admin-catalog", "admin-responses", "moderation-queue"];
  const clinicPages = ["clinic-dashboard", "clinic-product-detail", "clinic-verification", "verify-qr", "app/verify", "org-settings", "app/org/settings", "org-members", "app/org/members"];
  
  // Check if current page requires authentication
  const requiresAuth = protectedPages.includes(currentPage) || adminPages.includes(currentPage) || clinicPages.includes(currentPage);
  const requiresAdmin = adminPages.includes(currentPage);
  const requiresClinic = clinicPages.includes(currentPage);
  const requiresConsent = ["discover", "submit-review", "products", "clinics"].includes(currentPage);
  
  // Show loading state while checking auth (skip in demo mode)
  if (authLoading && requiresAuth && !isDemoMode) {
    // Check if we're processing a magic link
    const hash = window.location.hash.slice(1);
    const isMagicLink = hash.includes('access_token=') || hash.includes('type=magiclink');
    
    return (
      <div className="min-h-screen bg-background flex items-center justify-center gradient-mesh">
        <div className="text-center space-y-4 p-8 bg-card rounded-lg shadow-lg max-w-md">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-2">
              {isMagicLink ? 'Processing magic link...' : 'Verifying your login...'}
            </h2>
            <p className="text-muted-foreground">
              {isMagicLink 
                ? 'Capturing authentication tokens from your email link'
                : 'Please wait while we authenticate your session'
              }
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  // Redirect logic after auth loads
  useEffect(() => {
    if (!authLoading) {
      // 🔧 DEMO MODE: Skip all auth checks
      if (isDemoMode) {
        console.log('🔧 Demo mode active - bypassing auth checks');
        return;
      }
      
      // If user tries to access protected page without auth, redirect to signin
      if (requiresAuth && !user) {
        console.log('🔒 Protected page - redirecting to signin');
        handleNavigate("signin");
        return;
      }
      
      // If admin page requires admin role
      if (requiresAdmin && profile?.role !== 'admin') {
        console.log('🔒 Admin page - redirecting to discover');
        handleNavigate("discover");
        return;
      }
      
      // If clinic page requires clinic role
      if (requiresClinic && profile?.role !== 'clinic') {
        console.log('🔒 Clinic page - redirecting to discover');
        handleNavigate("discover");
        return;
      }
      
      // 🚀 CRITICAL: Wait for auth delay before redirecting authenticated users
      if (authPages.includes(currentPage) && user) {
        if (!authDelayComplete && !hasShownSuccessToast) {
          console.log('⏳ Waiting for auth delay to complete before redirecting...');
          return; // Don't redirect yet
        }
        
        console.log('✅ Already logged in - redirecting from auth page');
        
        // Check if user has completed consent
        if (!profile?.consented_at) {
          console.log('⚠️ No consent - redirecting to consent page');
          handleNavigate("consent");
        } else {
          console.log('✅ Has consent - redirecting to discover');
          handleNavigate("discover");
        }
        return;
      }
      
      // If user needs consent before accessing certain pages
      if (requiresConsent && user && !profile?.consented_at && currentPage !== "consent") {
        console.log('⚠️ Consent required - redirecting to consent page');
        handleNavigate("consent");
        return;
      }
    }
  }, [authLoading, user, profile, currentPage, requiresAuth, requiresAdmin, requiresClinic, requiresConsent, authDelayComplete, hasShownSuccessToast, handleNavigate, isDemoMode]);

  // Don't show header/footer on auth pages
  const isAuthPage = authPages.includes(currentPage) || currentPage === "consent";

  return (
    <div className="min-h-screen bg-background">
      <A11yEnhancements />
      <RolePreviewBanner />
      {!isAuthPage && (
        <Header
          currentPage={currentPage}
          onNavigate={handleNavigate}
        />
      )}
      <main id="main-content" tabIndex={-1}>{renderPage()}</main>
      {!isAuthPage && <Footer onNavigate={handleNavigate} />}
      <Toaster position="top-right" />
      {/* DevModePanel only visible on admin pages when enabled */}
      {adminPages.includes(currentPage) && isEnabled('devModePanel') && <DevModePanel onNavigate={handleNavigate} />}
    </div>
  );
}

// Root App component with all providers
export default function App() {
  return (
    <FeatureFlagsProvider>
      <DemoModeProvider>
        <AuthProvider>
          <RolePreviewProvider>
            <AppContent />
          </RolePreviewProvider>
        </AuthProvider>
      </DemoModeProvider>
    </FeatureFlagsProvider>
  );
}